import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import styles from './RemoteOralExam.scss';
import { t } from '../../services/i18nService';
import JoinController from './JoinController';
import remoteOralExamService from '../../services/RemoteOralExamService';

const REANNOUNCE_INTERVAL_MS = 30000;
const FIRST_ANNOUNCEMENT_DELAY_MS = 100;

const WaitingRoom = ({ isCandidateJoined, isInProgress, allShownChecksCompleted, status = {} }) => {
    const fallbackMessage = t('remoteOralExam.waitingRoom.examinerWillBeShortly');
    const statusMessage = status.statusMessage || fallbackMessage;
    const [announcedStatusMessage, setAnnouncedStatusMessage] = useState('');
    const [reannounceGeneration, setReannounceGeneration] = useState(0);
    const hasAnnouncedOnceRef = useRef(false);

    useEffect(() => {
        if (allShownChecksCompleted) {
            remoteOralExamService.scheduleFetchingOfSessionInfo();
        }
    }, [allShownChecksCompleted]);

    // Region mounts empty; first fill needs a short delay so AT registers it before the announcement lands.
    // Later updates land on an already-announced region, so those apply immediately (no delay needed).
    useEffect(() => {
        if (!isCandidateJoined) {
            hasAnnouncedOnceRef.current = false;
            return undefined;
        }

        if (!hasAnnouncedOnceRef.current) {
            const timeoutId = setTimeout(() => {
                hasAnnouncedOnceRef.current = true;
                setAnnouncedStatusMessage(statusMessage);
            }, FIRST_ANNOUNCEMENT_DELAY_MS);

            return () => clearTimeout(timeoutId);
        }

        setAnnouncedStatusMessage(statusMessage);
        return undefined;
    }, [isCandidateJoined, statusMessage]);

    // Narrator only reliably re-announces a live region when it is mounted as a brand-new node;
    // mutating the text of an already-mounted node (even clearing it first) is silently ignored.
    // So every reminder tick clears the text and bumps reannounceGeneration, which changes the
    // <p>'s key below and forces React to unmount/remount it, matching the proven announce-on-mount pattern.
    useEffect(() => {
        if (!isCandidateJoined || !allShownChecksCompleted) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            setAnnouncedStatusMessage('');
            setReannounceGeneration(previousValue => previousValue + 1);
        }, REANNOUNCE_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [isCandidateJoined, allShownChecksCompleted]);

    // Fills the freshly remounted (empty) live region once it has had a chance to register with AT.
    useEffect(() => {
        if (!isCandidateJoined || !allShownChecksCompleted || reannounceGeneration === 0) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            setAnnouncedStatusMessage(statusMessage);
        }, FIRST_ANNOUNCEMENT_DELAY_MS);

        return () => clearTimeout(timeoutId);
    }, [allShownChecksCompleted, isCandidateJoined, reannounceGeneration, statusMessage]);


    const getWaitingRoomContent = () => {
        if (isCandidateJoined) {
            return (<div className={styles.description}>
                <p>
                    <span className={styles.iconIndent}>
                        {statusMessage}
                    </span>
                </p>
            </div>);
        }

        return <JoinController isInProgress={isInProgress} />;
    };

    return (<div className={styles.wrapper}>
        <h3>{t('remoteOralExam.waitingRoom.label')}</h3>
        {/* Keyed by isCandidateJoined + reannounceGeneration so this remounts as a brand-new node on join
            and on every reminder tick, matching JoinController's reliable announce-on-mount for Narrator */}
        {allShownChecksCompleted &&
            <p
                key={`${isCandidateJoined}-${reannounceGeneration}`}
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="sr-only"
            >
                {announcedStatusMessage}
            </p>
        }
        { allShownChecksCompleted && getWaitingRoomContent()}
    </div>
    );
};

WaitingRoom.propTypes = {
    allShownChecksCompleted: PropTypes.bool.isRequired,
    isCandidateJoined: PropTypes.bool.isRequired,
    isInProgress: PropTypes.bool.isRequired,
    status: PropTypes.object,
};

export default WaitingRoom;
